import { HttpClient, HttpContext } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../environments/environment';
import { IS_UPLOAD_REQUEST } from 'src/app/types/types';

interface RecipientRow {
  number: string;
  variables: string[];
}

@Component({
  selector: 'app-campaign-setup',
  templateUrl: './campaign-setup.component.html',
  styleUrls: ['./campaign-setup.component.scss']
})
export class CampaignSetupComponent implements OnInit {

  loadingTemplates = false;
  sending = false;
  parsing = false;

  approvedTemplates: any[] = [];

  campaignName = '';
  selectedTemplateName = '';
  selectedLanguage = 'en_US';

  // Image-header template support
  templateNeedsImage = false;
  headerImageId = '';
  headerImageName = '';
  uploadingHeader = false;

  // Personalized image ("Dear <name>") - on by default for image-header templates
  personalizeImage = true;
  baseImageToken = '';

  pastedNumbers = '';
  fileRecipients: RecipientRow[] = []; // rows parsed from an uploaded file

  constructor(private http: HttpClient, private toastr: ToastrService,
    private route: ActivatedRoute, private router: Router) { }

  ngOnInit(): void {
    this.loadApprovedTemplates();
    const qp = this.route.snapshot.queryParams;
    if (qp['templateName']) { this.selectedTemplateName = qp['templateName']; }
    if (qp['language']) { this.selectedLanguage = qp['language']; }
  }

  loadApprovedTemplates(): void {
    this.loadingTemplates = true;
    this.http.get<any>(`${environment.serverUrl}/marketing/templates?limit=200&status=APPROVED`).subscribe({
      next: (res) => {
        this.approvedTemplates = res?.data || [];
        this.loadingTemplates = false;
        // If a template was pre-selected (e.g. via "Use in Campaign"), detect
        // now whether it needs a header image, since the change event won't fire.
        if (this.selectedTemplateName) {
          this.onTemplateChange();
        }
      },
      error: (e) => {
        this.loadingTemplates = false;
        console.error('Load approved templates failed', e);
        this.toastr.error(e?.error?.error || e?.message || 'Failed to load templates', 'Error');
      }
    });
  }

  onTemplateChange(): void {
    const t = this.approvedTemplates.find(x => x.name === this.selectedTemplateName);
    // Reset any previously chosen header image.
    this.headerImageId = '';
    this.baseImageToken = '';
    this.headerImageName = '';
    this.personalizeImage = true;
    this.templateNeedsImage = false;
    if (t) {
      this.selectedLanguage = t.language;
      const header = (t.components || []).find((c: any) => (c.type || '').toUpperCase() === 'HEADER');
      this.templateNeedsImage = !!header && (header.format || '').toUpperCase() === 'IMAGE';
    }
  }

  onPersonalizeToggle(): void {
    // Switching mode invalidates any previously uploaded image.
    this.headerImageId = '';
    this.baseImageToken = '';
    this.headerImageName = '';
  }

  // Max image size (KB). Keep in sync with WA_MAX_IMAGE_KB in the backend .env.
  readonly maxImageKb = 2048;

  onHeaderImageSelected(event: any): void {
    const file = event?.target?.files?.[0];
    if (!file) { return; }
    if (file.size > this.maxImageKb * 1024) {
      event.target.value = '';
      this.toastr.warning(
        `Image is too large (${Math.round(file.size / 1024)} KB). Please use one under ${this.maxImageKb} KB.`,
        'Image too large'
      );
      return;
    }
    const formData = new FormData();
    formData.append('file', file);

    // Personalized campaigns upload a BASE image (kept on the server to
    // re-composite per recipient); otherwise upload directly to Meta as media.
    const url = this.personalizeImage
      ? `${environment.serverUrl}/marketing/campaign-base-image`
      : `${environment.serverUrl}/marketing/send-media`;

    this.uploadingHeader = true;
    this.http.post<any>(url, formData, {
      context: new HttpContext().set(IS_UPLOAD_REQUEST, true)
    }).subscribe({
      next: (res) => {
        this.uploadingHeader = false;
        if (this.personalizeImage) {
          this.baseImageToken = res?.baseImageToken || '';
        } else {
          this.headerImageId = res?.mediaId || '';
        }
        this.headerImageName = file.name;
        this.toastr.success('Image uploaded', 'Ready');
        // Note: we intentionally do NOT clear the file input here, so the chosen
        // file stays visible and the base image doesn't appear to be removed.
      },
      error: (e) => {
        this.uploadingHeader = false;
        event.target.value = '';
        console.error('Header image upload failed', e);
        this.toastr.error(e?.error?.error || e?.message || 'Failed to upload image', 'Error');
      }
    });
  }

  onFileSelected(event: any): void {
    const file = event?.target?.files?.[0];
    if (!file) { return; }
    const formData = new FormData();
    formData.append('file', file);

    this.parsing = true;
    this.http.post<any>(`${environment.serverUrl}/marketing/extract-numbers`, formData, {
      context: new HttpContext().set(IS_UPLOAD_REQUEST, true)
    }).subscribe({
      next: (res) => {
        this.parsing = false;
        this.fileRecipients = res?.recipients || [];
        this.toastr.success(`Extracted ${this.fileRecipients.length} rows from file`, 'Parsed');
        event.target.value = '';
      },
      error: (e) => {
        this.parsing = false;
        event.target.value = '';
        console.error('Extract numbers failed', e);
        this.toastr.error(e?.error?.error || e?.message || 'Failed to parse file', 'Error');
      }
    });
  }

  clearFile(): void {
    this.fileRecipients = [];
  }

  /** Each pasted line: "number, var1, var2" -> number + variables. */
  private parsePasted(): RecipientRow[] {
    if (!this.pastedNumbers) { return []; }
    const rows: RecipientRow[] = [];
    for (const line of this.pastedNumbers.split(/\r?\n/)) {
      if (!line.trim()) { continue; }
      const tokens = line.split(',').map(t => t.trim()).filter(t => t.length > 0);
      if (tokens.length === 0) { continue; }
      const number = tokens[0].replace(/[^0-9]/g, '');
      if (number.length < 8 || number.length > 15) { continue; }
      rows.push({ number, variables: tokens.slice(1) });
    }
    return rows;
  }

  private allRecipients(): RecipientRow[] {
    const seen = new Set<string>();
    const result: RecipientRow[] = [];
    [...this.parsePasted(), ...this.fileRecipients].forEach(r => {
      const d = (r.number || '').replace(/[^0-9]/g, '');
      if (d.length >= 8 && d.length <= 15 && !seen.has(d)) {
        seen.add(d);
        result.push({ number: d, variables: r.variables || [] });
      }
    });
    return result;
  }

  get recipients(): RecipientRow[] {
    return this.allRecipients();
  }

  get recipientCount(): number {
    return this.allRecipients().length;
  }

  get maxVariables(): number {
    return this.allRecipients().reduce((m, r) => Math.max(m, r.variables.length), 0);
  }

  send(): void {
    if (!this.campaignName.trim()) { this.toastr.warning('Campaign name is required', 'Missing'); return; }
    if (!this.selectedTemplateName) { this.toastr.warning('Please select a template', 'Missing'); return; }

    const recipients = this.allRecipients();
    if (recipients.length === 0) { this.toastr.warning('Add at least one phone number', 'Missing'); return; }

    if (this.templateNeedsImage) {
      if (this.personalizeImage && !this.baseImageToken) {
        this.toastr.warning('Please upload a base image for personalization.', 'Missing');
        return;
      }
      if (!this.personalizeImage && !this.headerImageId) {
        this.toastr.warning('This template has an image header. Please upload a header image.', 'Missing');
        return;
      }
    }

    const payload: any = {
      name: this.campaignName.trim(),
      template: {
        name: this.selectedTemplateName,
        language: this.selectedLanguage
      },
      recipients
    };
    if (this.templateNeedsImage) {
      if (this.personalizeImage) {
        payload.personalizeImage = true;
        payload.baseImageToken = this.baseImageToken;
      } else {
        payload.headerImageId = this.headerImageId;
      }
    }

    this.sending = true;
    this.http.post<any>(`${environment.serverUrl}/marketing/campaigns`, JSON.stringify(payload)).subscribe({
      next: (campaign) => {
        this.sending = false;
        this.toastr.success(`Campaign started for ${recipients.length} recipients`, 'Sending');
        this.router.navigate(['/whatsapp-bulk/campaigns', campaign.id]);
      },
      error: (e) => {
        this.sending = false;
        console.error('Create campaign failed', e);
        this.toastr.error(e?.error?.error || e?.message || 'Failed to start campaign', 'Error');
      }
    });
  }
}
